import {
  COMPLETION_NOT_STARTED,
  COMPLETION_STARTED,
  COMPLETION_COMPLETE,
} from "%/dynamic-component-editor/constants/completion-status-values";
import { completedPercent } from "%/dynamic-component-editor/standard-components/base-story-card/completion-progress-percent";

export const OVERLAY_COUNT_FIELDS = Object.freeze([
  "notStartedChildNodes",
  "startedChildNodes",
  "completedChildNodes",
  "notStartedLeafNodesInTree",
  "startedLeafNodesInTree",
  "completedLeafNodesInTree",
]);

export function emptyOverlay() {
  return { ...Object.fromEntries(OVERLAY_COUNT_FIELDS.map(field => [field, 0])), percentFraction: 0 };
}

export function statusDelta(from, to) {
  const started = (to === COMPLETION_STARTED ? 1 : 0) - (from === COMPLETION_STARTED ? 1 : 0);
  const complete = (to === COMPLETION_COMPLETE ? 1 : 0) - (from === COMPLETION_COMPLETE ? 1 : 0);
  return { started, complete, notStarted: -(started + complete) };
}

export function folderStatusFromChildCounts(counts) {
  const children = Number(counts?.childNodes) || 0;
  const started = Number(counts?.startedChildNodes) || 0;
  const completed = Number(counts?.completedChildNodes) || 0;
  if (children <= 0) {
    return null;
  }
  if (completed >= children) {
    return COMPLETION_COMPLETE;
  }
  return started + completed > 0 ? COMPLETION_STARTED : COMPLETION_NOT_STARTED;
}

export function addOverlay(into, delta, sign = 1) {
  const result = { ...emptyOverlay(), ...into };
  for (const field of [...OVERLAY_COUNT_FIELDS, "percentFraction"]) {
    result[field] += sign * (delta?.[field] || 0);
  }
  return result;
}

export function isEmptyOverlay(overlay) {
  return !overlay || [...OVERLAY_COUNT_FIELDS, "percentFraction"].every(field => Math.abs(overlay[field] || 0) < 1e-9);
}

export function applyOverlay(baseline, overlay) {
  if (!baseline || isEmptyOverlay(overlay)) {
    return baseline;
  }
  const shown = { ...baseline };
  for (const field of OVERLAY_COUNT_FIELDS) {
    if (shown[field] != null) {
      shown[field] = Math.max(0, shown[field] + overlay[field]);
    }
  }
  if (shown.completedChildNodes != null) {
    shown.completed = shown.completedChildNodes;
    shown.completedChildNodesPercent = completedPercent(shown.completedChildNodes, shown.childNodes);
  }
  if (shown.completedLeafNodesInTree != null) {
    shown.completedLeafNodesPercent = completedPercent(shown.completedLeafNodesInTree, shown.leafNodesInTree);
  }
  if (typeof shown.percent === "number") {
    const percentDelta = overlay.percentFraction * 100;
    shown.percent = Math.min(100, Math.max(0, Math.round(shown.percent + percentDelta)));
  }
  return shown;
}

export function ancestorOverlayDeltas({ from, to, chain }) {
  const deltas = new Map();
  const leaf = statusDelta(from, to);
  let child = leaf;
  let fraction = (to === COMPLETION_COMPLETE ? 1 : 0) - (from === COMPLETION_COMPLETE ? 1 : 0);
  let childKnown = true;
  for (const { folderId, counts } of chain) {
    if (!counts) {
      childKnown = false;
      continue;
    }
    const delta = emptyOverlay();
    delta.startedLeafNodesInTree = leaf.started;
    delta.completedLeafNodesInTree = leaf.complete;
    delta.notStartedLeafNodesInTree = leaf.notStarted;
    if (childKnown) {
      delta.startedChildNodes = child.started;
      delta.completedChildNodes = child.complete;
      delta.notStartedChildNodes = child.notStarted;
      const children = Number(counts.childNodes) || 0;
      fraction = children > 0 ? fraction / children : 0;
      delta.percentFraction = fraction;
      const before = folderStatusFromChildCounts(counts);
      const after = folderStatusFromChildCounts(applyOverlay(counts, delta));
      child = statusDelta(before, after);
      childKnown = before !== null && after !== null;
    }
    deltas.set(folderId, delta);
  }
  return deltas;
}
